import React, { useEffect, useRef } from 'react';
import { Button, Radio, DatePicker, Select } from 'antd';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { getSmartLabelClassName } from '../../../../../utils/labelWidthUtils';
import { useFilterContainerClassName, useFilterLabelClassName, useFilterInputContainerClassName } from '../../../../../utils/languageUtils';
import ParcelFilterStyle from '../../../../delivery/components/ParcelFilterStyle';
import { type ArrivalPointType, ARRIVAL_POINT_TYPE_WHS, ARRIVAL_POINT_TYPE_HUB } from '../constants';
import { DriverFilterOption } from '../../../../../utils/driverFilterOption';

/** 远程搜索防抖间隔，与 components/SearchSelect 默认值保持一致 */
const SEARCH_DEBOUNCE_MS = 300;

export type DateRangePreset = 'today' | 'yesterday' | 'last3days' | 'last7days' | 'last15days' | 'custom';

export function getDateRangeByPreset(preset: DateRangePreset, customRange?: [Dayjs, Dayjs]): [Dayjs, Dayjs] {
    const today = dayjs().startOf('day');
    if (preset === 'custom' && customRange?.[0] && customRange?.[1]) {
        return [customRange[0].startOf('day'), customRange[1].startOf('day')];
    }
    switch (preset) {
        case 'today':
            return [today, today];
        case 'yesterday': {
            const y = today.subtract(1, 'day');
            return [y, y];
        }
        case 'last3days':
            return [today.subtract(2, 'day'), today];
        case 'last7days': {
            const yesterday = today.subtract(1, 'day');
            return [yesterday.subtract(6, 'day'), yesterday];
        }
        case 'last15days': {
            const yesterday = today.subtract(1, 'day');
            return [yesterday.subtract(14, 'day'), yesterday];
        }
        default:
            return [today.subtract(2, 'day'), today];
    }
}

export type SlaMonitoringViewMode = 'driver' | 'area';

export interface SlaMonitoringFilters {
    dateRangePreset: DateRangePreset;
    dateRange: [dayjs.Dayjs, dayjs.Dayjs];
    arrivalDate: dayjs.Dayjs;
    selectedCities: string[];
    selectedDsps: string[];
    selectedAreas: string[];
    selectedDrivers: string[];
    arrivalPointType: ArrivalPointType;
    arrivalTimeDateRange: [dayjs.Dayjs, dayjs.Dayjs] | null; // 到达时间日期范围（限制过去60天，用于所有选项包括快捷选项和自定义）
    arrivalTimeDatePreset: 'today' | 'yesterday' | 'last7days' | 'last15days' | 'custom' | null; // 当前选择的快捷选项或自定义
    viewMode: SlaMonitoringViewMode;
}

interface SlaMonitoringFilterProps {
    filters: SlaMonitoringFilters;
    onFiltersChange: (filters: SlaMonitoringFilters) => void;
    dsps: Array<{ id: number; name: string }>; // 从汇总表提取的DSP数据
    areas: Array<{ id: number; name: string }>; // 从汇总表提取的区域数据
    drivers: DriverFilterOption[]; // 司机下拉候选（司机维度时显示）
    viewMode: SlaMonitoringViewMode; // 区域维度时显示区域下拉，司机维度时显示司机下拉
    showActions?: boolean; // 是否显示按钮区（默认显示）
    onReset?: () => void; // 重置回调（可选，默认使用内部重置逻辑）
    dspMode?: boolean; // DSP 模式（路由 mode 派生）：为真时隐藏 DSP 下拉
    /** DSP 远程搜索；传入时 DSP 下拉由后端按关键词返回候选 */
    onDspSearch?: (keyword: string) => void;
    /** 区域远程搜索；传入时区域下拉由后端按关键词返回候选 */
    onAreaSearch?: (keyword: string) => void;
    /** 司机远程搜索；传入时司机下拉由后端按关键词返回候选 */
    onDriverSearch?: (keyword: string) => void;
    dspOptionsLoading?: boolean;
    areaOptionsLoading?: boolean;
    driverOptionsLoading?: boolean;
}

const SlaMonitoringFilter: React.FC<SlaMonitoringFilterProps> = ({
    filters,
    onFiltersChange,
    dsps,
    areas,
    drivers,
    viewMode,
    showActions = true,
    onReset,
    dspMode = false,
    onDspSearch,
    onAreaSearch,
    onDriverSearch,
    dspOptionsLoading,
    areaOptionsLoading,
    driverOptionsLoading
}) => {
    const { t, i18n } = useTranslation();
    // 第一行第三列（时间范围）左边距：中文收拢减小 gap，外语右移避免与 Custom 按钮重叠
    const dateCol3MarginLeft = i18n.language?.startsWith('zh') ? '-5rem' : '3rem';

    // 区域/司机为远程搜索，逐字符请求会打满后端，故与 SearchSelect 一致做 300ms 防抖。
    // 区域与司机由 viewMode 二选一渲染，同时只存在一个下拉，可共用同一个定时器。
    const searchDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => {
        if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
    }, []);
    const withSearchDebounce = (handler?: (keyword: string) => void) => {
        if (!handler) return undefined;
        return (keyword: string) => {
            if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
            searchDebounceTimer.current = setTimeout(() => handler(keyword), SEARCH_DEBOUNCE_MS);
        };
    };

    // 只在下拉收起时把关键词清回空，下次点开是范围内的默认列表。
    // 选中项时不能清：antd 多选选中后会自行清空输入框文本但不回调 onSearch，
    // 此时若跟着把关键词也清掉，候选会立刻回落成默认前 50 条，
    // 用户没法在同一批搜索结果里连续勾选（输入 f 选中 F052 后就看不到 F054 了）。
    const resetSearchKeyword = (handler?: (keyword: string) => void) => {
        if (!handler) return;
        if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
        handler('');
    };
    const withKeywordResetOnClose = (handler?: (keyword: string) => void) => {
        if (!handler) return undefined;
        return (open: boolean) => {
            if (!open) resetSearchKeyword(handler);
        };
    };

    const labelArrivalTimeType = String(t('wms.delivery.parcelSlaMonitoring.buttons.arrivalTimeType'));
    const labelArrivalTimeDate = String(t('wms.delivery.parcelSlaMonitoring.buttons.arrivalTimeDate'));
    const labelDsp = String(t('wms.delivery.parcelSlaMonitoring.buttons.dsp'));
    const labelArea = String(t('wms.delivery.parcelSlaMonitoring.buttons.area'));
    const labelDriver = String(t('wms.delivery.parcelSlaMonitoring.buttons.driver'));
    // 第一列：日期类型、DSP标题对齐
    const col1Labels = [labelArrivalTimeType, labelDsp];
    // 第二列：日期 + 区域/司机（按 viewMode 显示其一）
    const col2Labels = [labelArrivalTimeDate, viewMode === 'area' ? labelArea : labelDriver];
    
    // 使用布局工具函数确保对齐
    const col1ContainerClassName = useFilterContainerClassName();
    const col1LabelClassName = useFilterLabelClassName(getSmartLabelClassName(col1Labels));
    const col1InputContainerClassName = useFilterInputContainerClassName();
    
    const col2ContainerClassName = useFilterContainerClassName();
    const col2LabelClassName = useFilterLabelClassName(getSmartLabelClassName(col2Labels));
    const col2InputContainerClassName = useFilterInputContainerClassName();
    
    // 计算当前选择的快捷选项（如果用户没有明确选择，则根据日期范围推断）
    const getCurrentPreset = (): 'today' | 'yesterday' | 'last7days' | 'last15days' | 'custom' => {
        // 如果用户明确选择了预设，使用用户的选择
        if (filters.arrivalTimeDatePreset) {
            return filters.arrivalTimeDatePreset;
        }

        // 否则根据日期范围推断
        if (!filters.arrivalTimeDateRange) return 'today';
        const today = dayjs().startOf('day');
        const [startDate, endDate] = filters.arrivalTimeDateRange;
        const start = startDate.startOf('day');
        const end = endDate.startOf('day');

        const yesterday = today.subtract(1, 'day');
        if (start.isSame(today) && end.isSame(today)) return 'today';
        if (start.isSame(yesterday) && end.isSame(yesterday)) return 'yesterday';
        if (start.isSame(yesterday.subtract(6, 'day')) && end.isSame(yesterday)) return 'last7days';
        if (start.isSame(yesterday.subtract(14, 'day')) && end.isSame(yesterday)) return 'last15days';

        // 其他情况都是自定义
        return 'custom';
    };
    
    const currentPreset = getCurrentPreset();
    const isCustom = currentPreset === 'custom';

    const handlePresetChange = (preset: DateRangePreset) => {
        const range = getDateRangeByPreset(preset, preset === 'custom' ? filters.dateRange : undefined);
        onFiltersChange({
            ...filters,
            dateRangePreset: preset,
            dateRange: range,
            arrivalDate: range[1]
        });
    };

    const handleRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        if (!dates || !dates[0] || !dates[1]) return;
        const range: [Dayjs, Dayjs] = [dates[0].startOf('day'), dates[1].startOf('day')];
        onFiltersChange({
            ...filters,
            dateRangePreset: 'custom',
            dateRange: range,
            arrivalDate: range[1]
        });
    };

    const handleReset = () => {
        const range = getDateRangeByPreset('last3days');
        const today = dayjs().startOf('day');
        onFiltersChange({
            ...filters,
            dateRangePreset: 'last3days',
            dateRange: range,
            arrivalDate: range[1],
            selectedDsps: [],
            selectedAreas: [],
            selectedDrivers: [],
            arrivalPointType: ARRIVAL_POINT_TYPE_WHS,
            arrivalTimeDateRange: [today, today],
            arrivalTimeDatePreset: 'today'
        });
    };

    // 过去30天内可选（含今天），用于 RangePicker
    const getDisabledDateRange = (current: dayjs.Dayjs | null) => {
        if (!current) return false;
        const today = dayjs().startOf('day');
        const minDate = today.subtract(30, 'day');
        return current.isAfter(today) || current.isBefore(minDate);
    };

    // 处理到达时间日期快捷选项（返回日期范围）
    const handleArrivalTimeDatePreset = (preset: 'today' | 'yesterday' | 'last7days' | 'last15days') => {
        const today = dayjs().startOf('day');
        let dateRange: [dayjs.Dayjs, dayjs.Dayjs];
        switch (preset) {
            case 'today':
                dateRange = [today.clone(), today.clone()];
                break;
            case 'yesterday': {
                const yesterday = today.subtract(1, 'day');
                dateRange = [yesterday.clone(), yesterday.clone()];
                break;
            }
            case 'last7days': {
                const yesterday = today.subtract(1, 'day');
                dateRange = [yesterday.subtract(6, 'day').clone(), yesterday.clone()];
                break;
            }
            case 'last15days': {
                const yesterday = today.subtract(1, 'day');
                dateRange = [yesterday.subtract(14, 'day').clone(), yesterday.clone()];
                break;
            }
            default:
                dateRange = [today.clone(), today.clone()];
        }
        // 确保每次调用都创建新的数组和 dayjs 对象，以触发 React 的状态更新
        onFiltersChange({
            ...filters,
            arrivalTimeDateRange: dateRange,
            arrivalTimeDatePreset: preset
        });
    };
    
    return (
        <div className="mb-2 pl-0 py-2">
            <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1">
                    {/* 两行共用一个 grid，固定列间距 2.5rem，保证第一列与第二列之间的 gap 一致 */}
                    <div className="grid gap-x-[2.5rem] gap-y-4 items-start mb-4" style={{ gridTemplateColumns: 'calc((100% - 5rem) / 3) calc((100% - 5rem) / 3) calc((100% - 5rem) / 3 - 20px)' }}>
                        {/* 第一行第一列：日期类型（占第一格） */}
                        <div className="min-w-0 w-full">
                            <div className={col1ContainerClassName}>
                                <span className={col1LabelClassName}>{labelArrivalTimeType}</span>
                                <div className={col1InputContainerClassName}>
                                    <Select
                                        value={filters.arrivalPointType ?? ARRIVAL_POINT_TYPE_WHS}
                                        onChange={(value: ArrivalPointType) => onFiltersChange({...filters, arrivalPointType: value})}
                                        className="w-full"
                                        size="middle"
                                        placeholder={labelArrivalTimeType || undefined}
                                        allowClear={false}
                                    >
                                        <Select.Option value={ARRIVAL_POINT_TYPE_WHS}>{t('wms.delivery.parcelSlaMonitoring.columns.arrivalTimeTypeWhs')}</Select.Option>
                                        <Select.Option value={ARRIVAL_POINT_TYPE_HUB}>{t('wms.delivery.parcelSlaMonitoring.columns.arrivalTimeTypeHub')}</Select.Option>
                                    </Select>
                                </div>
                            </div>
                        </div>
                        {/* 第一行第二列：日期 + 按钮组（与 Area 同列同宽） */}
                        <div className="min-w-0 w-full">
                            <div className={`${col2ContainerClassName} w-full`}>
                                <span className={col2LabelClassName}>{labelArrivalTimeDate}</span>
                                <div className={col2InputContainerClassName}>
                                    <Radio.Group
                                        optionType="button"
                                        buttonStyle="solid"
                                        size="middle"
                                        value={currentPreset}
                                        onChange={(e) => {
                                            const value = e.target.value as 'today' | 'yesterday' | 'last7days' | 'last15days' | 'custom';
                                            if (value === 'custom') {
                                                const today = dayjs().startOf('day');
                                                const currentRange = filters.arrivalTimeDateRange || [today, today];
                                                onFiltersChange({
                                                    ...filters,
                                                    arrivalTimeDateRange: [currentRange[0].clone(), currentRange[1].clone()],
                                                    arrivalTimeDatePreset: 'custom'
                                                });
                                            } else {
                                                handleArrivalTimeDatePreset(value);
                                            }
                                        }}
                                        className="w-full flex flex-nowrap items-center justify-start [&_.ant-radio-button-wrapper]:whitespace-nowrap [&_.ant-radio-button-wrapper]:!px-2 [&_.ant-radio-button-wrapper]:!py-1 [&_.ant-radio-button-wrapper]:!flex-1 [&_.ant-radio-button-wrapper]:!flex [&_.ant-radio-button-wrapper]:!items-center [&_.ant-radio-button-wrapper]:!justify-center [&_.ant-radio-button-wrapper-checked]:!bg-white [&_.ant-radio-button-wrapper-checked]:!border [&_.ant-radio-button-wrapper-checked]:!border-[#722ed1] [&_.ant-radio-button-wrapper-checked]:!text-[#722ed1] [&_.ant-radio-button-wrapper-checked:hover]:!bg-white [&_.ant-radio-button-wrapper-checked:hover]:!border-[#722ed1] [&_.ant-radio-button-wrapper-checked:hover]:!text-[#722ed1]"
                                    >
                                        <Radio.Button value="today">{t('wms.delivery.parcelSlaMonitoring.buttons.dateRangeToday')}</Radio.Button>
                                        <Radio.Button value="yesterday">{t('wms.delivery.parcelSlaMonitoring.buttons.dateRangeYesterday')}</Radio.Button>
                                        <Radio.Button value="last7days">{t('wms.delivery.parcelSlaMonitoring.buttons.dateRangeLast7Days')}</Radio.Button>
                                        <Radio.Button value="last15days">{t('wms.delivery.parcelSlaMonitoring.buttons.dateRangeLast15Days')}</Radio.Button>
                                        <Radio.Button value="custom">{t('wms.delivery.parcelSlaMonitoring.buttons.dateRangeCustom')}</Radio.Button>
                                    </Radio.Group>
                                </div>
                            </div>
                        </div>
                        {/* 第一行第三列：时间范围，间距随语言微调 */}
                        <div className="min-w-0 w-full" style={{ marginLeft: dateCol3MarginLeft }}>
                            <div className={`${col2ContainerClassName} w-full`}>
                                <span className={col2LabelClassName} aria-hidden style={{ visibility: 'hidden' }}>{labelArrivalTimeDate}</span>
                                <div className={col2InputContainerClassName}>
                                    <DatePicker.RangePicker
                                        value={filters.arrivalTimeDateRange}
                                        onChange={(dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
                                            if (dates && dates[0] && dates[1]) {
                                                onFiltersChange({
                                                    ...filters,
                                                    arrivalTimeDateRange: [dates[0].startOf('day'), dates[1].startOf('day')],
                                                    arrivalTimeDatePreset: 'custom'
                                                });
                                            }
                                        }}
                                        className="w-full"
                                        size="middle"
                                        format="YYYY-MM-DD"
                                        disabledDate={getDisabledDateRange}
                                        placeholder={[labelArrivalTimeDate || '', labelArrivalTimeDate || '']}
                                        allowClear={false}
                                        disabled={!isCustom}
                                    />
                                </div>
                            </div>
                        </div>
                        {/* 第二行第一列：DSP 或占位（DSP 模式时为空） */}
                        {!dspMode ? (
                            <div className="min-w-0 w-full">
                                <ParcelFilterStyle
                                    label={labelDsp}
                                    options={dsps.map(dsp => ({ value: dsp.name, label: dsp.name }))}
                                    value={filters.selectedDsps}
                                    onChange={(value: string[]) => {
                                        onFiltersChange({...filters, selectedDsps: value});
                                    }}
                                    className="w-full"
                                    spanClassName={getSmartLabelClassName(col1Labels)}
                                    mode="multiple"
                                    showSearch
                                    onSearch={withSearchDebounce(onDspSearch)}
                                    onDropdownVisibleChange={withKeywordResetOnClose(onDspSearch)}
                                    filterOption={onDspSearch ? false : undefined}
                                    loading={dspOptionsLoading}
                                    placeholder={t('wms.delivery.parcelSlaMonitoring.buttons.dsp') || undefined}
                                />
                            </div>
                        ) : (
                            <div className="min-w-0" />
                        )}
                        {/* 第二行第二列：区域/司机（与第一行日期列左对齐、gap 一致） */}
                        <div className="min-w-0 w-full">
                            {viewMode === 'area' ? (
                                <ParcelFilterStyle
                                    label={labelArea}
                                    options={areas.map(area => ({ value: area.name, label: area.name }))}
                                    value={filters.selectedAreas}
                                    onChange={(value: string[]) => {
                                        onFiltersChange({...filters, selectedAreas: value});
                                    }}
                                    className="w-full"
                                    spanClassName={getSmartLabelClassName(col2Labels)}
                                    mode="multiple"
                                    showSearch
                                    onSearch={withSearchDebounce(onAreaSearch)}
                                    onDropdownVisibleChange={withKeywordResetOnClose(onAreaSearch)}
                                    filterOption={onAreaSearch ? false : undefined}
                                    loading={areaOptionsLoading}
                                    placeholder={t('wms.delivery.parcelSlaMonitoring.buttons.area') || undefined}
                                />
                            ) : (
                                <ParcelFilterStyle
                                    label={labelDriver}
                                    options={drivers.map(driver => ({ value: String(driver.accountId), label: driver.name, title: driver.title }))}
                                    value={filters.selectedDrivers}
                                    onChange={(value: string[]) => {
                                        onFiltersChange({...filters, selectedDrivers: value});
                                    }}
                                    className="w-full"
                                    spanClassName={getSmartLabelClassName(col2Labels)}
                                    mode="multiple"
                                    showSearch
                                    onSearch={withSearchDebounce(onDriverSearch)}
                                    onDropdownVisibleChange={withKeywordResetOnClose(onDriverSearch)}
                                    filterOption={onDriverSearch ? false : undefined}
                                    loading={driverOptionsLoading}
                                    placeholder={t('wms.delivery.parcelSlaMonitoring.buttons.driver') || undefined}
                                />
                            )}
                        </div>
                        {/* 第二行第三列：占位符 */}
                        <div className="min-w-0" />
                    </div>
                    {showActions && (
                        <div className="mb-1 flex justify-end gap-4 pl-0">
                            <Button type="default" onClick={onReset || handleReset} className="w-auto min-w-24">
                                <span className="font-bold">{t('components.button.reset')}</span>
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SlaMonitoringFilter;